import type { Editor } from '@tiptap/core';

import { findElementByAttribute } from '@shared/helpers';
import { DOCUMENT_HTML_ATTRIBUTES } from '../../constants';
import type { TResolvedElementRange } from './types';

/** A heading carries its id as a plain `id`, everything else as `data-id`; a caller may know which. */
export const findByIdAttribute = (dom: HTMLElement, id: string, preferred?: string): HTMLElement | null => {
  if (preferred) {
    const direct = findElementByAttribute(preferred, id, dom);
    if (direct) return direct;
  }
  for (const attribute of Object.values(DOCUMENT_HTML_ATTRIBUTES)) {
    if (attribute === preferred) continue;
    const found = findElementByAttribute(attribute, id, dom);
    if (found) return found;
  }
  return null;
};

const resolveNodeRangeById = (editor: Editor, id: string): TResolvedElementRange | null => {
  let resolved: TResolvedElementRange | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (resolved) return false;
    if (node.attrs.id !== id) return true;

    resolved = {
      id,
      from: pos,
      to: pos + node.nodeSize,
      element: findElementByAttribute(DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID, id, editor.view.dom),
    };
    return false;
  });

  return resolved;
};

export const resolveElementRangeById = (editor: Editor, id: string): TResolvedElementRange | null => {
  const resolvedNode = resolveNodeRangeById(editor, id);
  if (resolvedNode) return resolvedNode;

  const element = findByIdAttribute(editor.view.dom, id);
  if (!element) return null;

  try {
    const from = editor.view.posAtDOM(element, 0);
    // -1 means the editor does not hold this node; a range built on it corrupts every consumer.
    if (from < 0) return null;
    let to = from + 1;
    try {
      const end = editor.view.posAtDOM(element, element.childNodes.length);
      if (end > from) to = end;
    } catch {
      // childNodes-based end can fail for some nodes; keep the minimal range.
    }
    return {
      id,
      from,
      to: Math.min(editor.state.doc.content.size, to),
      element,
    };
  } catch {
    // posAtDOM throws when the matched element is outside this editor (a same id elsewhere on the page).
    return null;
  }
};

export const uniqueIds = (ids: Array<string | undefined>): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }

  return result;
};
